<template>
  <div class="p-6">
    <!-- Grid de cards -->
    <div class="grid grid-cols-1 gap-6 sm:grid-cols-2" :class="gridColsClass">
      <!-- Loading skeletons -->
      <template v-if="loading">
        <div
          v-for="n in skeletonCount"
          :key="'skeleton-' + n"
          class="relative w-[300px] sm:w-[320px] 2xl:w-[410px] max-w-xl h-60 rounded-3xl shadow-lg overflow-hidden animate-pulse"
        >
          <Skeleton class="absolute inset-0 w-full h-full" />
        </div>
      </template>

      <!-- Artist cards -->
      <template v-else>
        <div
          v-for="artist in artists"
          :key="artist.name"
          @click="artist.active && openModal(artist)"
          class="relative overflow-hidden transition-all duration-500 shadow-lg w-[300px] sm:w-[320px] 2xl:w-[410px] max-w-xl rounded-3xl group"
          :class="[
            artist.active
              ? 'cursor-pointer hover:scale-[1.03]'
              : 'opacity-60 grayscale cursor-not-allowed',
          ]"
          style="height: 240px"
        >
          <!-- Artist Image -->
          <NuxtImg
            :src="artist.image"
            alt=""
            loading="lazy"
            class="absolute inset-0 object-cover w-full h-full transition-all duration-700 ease-in-out transform"
            :class="
              artist.active
                ? 'grayscale contrast-110 brightness-90 group-hover:grayscale-0 group-hover:contrast-100 group-hover:brightness-100'
                : 'grayscale contrast-75 brightness-75'
            "
          />

          <!-- Overlay -->
          <div
            class="absolute inset-0 transition-all duration-500"
            :class="
              artist.active
                ? 'bg-black/30 group-hover:bg-black/10'
                : 'bg-black/50'
            "
          ></div>

          <!-- Gradient -->
          <div
            class="absolute bottom-0 left-0 right-0 h-32 transition-all duration-500 bg-gradient-to-t"
            :class="
              artist.active
                ? 'from-black/80 via-black/40 to-transparent group-hover:from-black/70 group-hover:via-black/30'
                : 'from-black/90 via-black/60 to-transparent'
            "
          ></div>

          <!-- Name -->
          <div
            class="absolute text-2xl font-bold text-white transition-all duration-300 select-none bottom-6 left-6 drop-shadow-lg"
            :class="
              artist.active
                ? 'translate-y-2 group-hover:translate-y-0'
                : 'opacity-80'
            "
          >
            {{ artist.name }}
            <span
              v-if="!artist.active"
              class="ml-2 text-sm font-normal text-red-300"
            >
              {{
                artist.releaseDate
                  ? new Date(artist.releaseDate).toLocaleDateString("pt-PT")
                  : $t("comingSoon")
              }}
            </span>
          </div>
        </div>
      </template>
    </div>

    <!-- Modal -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="-translate-y-4 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="-translate-y-4 opacity-0"
    >
      <div
        v-if="selectedArtist"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-transparent backdrop-blur-sm"
        @click.self="selectedArtist = null"
      >
        <div
          class="relative flex w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl md:flex-row md:max-h-[80vh]"
        >
          <!-- Close button -->
          <button
            @click="selectedArtist = null"
            class="absolute z-10 p-2 text-xl text-gray-500 transition-all duration-200 rounded-full cursor-pointer right-3 top-3 hover:bg-gray-100 hover:text-black md:right-4 md:top-4 focus:outline-none focus:ring-2 focus:ring-gray-200"
            aria-label="Close modal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <!-- Left image -->
          <div
            class="relative flex items-center justify-center flex-1 bg-gray-900 md:p-6 rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none"
          >
            <NuxtImg
              :src="selectedArtist.image"
              alt=""
              loading="lazy"
              class="absolute inset-0 object-cover w-full h-full blur-lg opacity-20"
            />
            <div
              class="relative z-10 w-48 h-48 overflow-hidden shadow-lg md:w-64 md:h-64 rounded-xl"
            >
              <NuxtImg
                :src="selectedArtist.image"
                alt=""
                class="object-cover w-full h-full transition-transform duration-300 hover:scale-105"
              />
            </div>
          </div>

          <!-- Right content -->
          <div class="flex flex-col flex-1 p-6 md:p-8">
            <h2 class="mb-4 text-2xl font-semibold text-gray-900 md:text-3xl">
              {{ selectedArtist.name }}
            </h2>
            <div
              class="flex-1 pr-2 mb-4 overflow-y-auto scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-300"
            >
              <p
                class="leading-relaxed text-gray-700 whitespace-pre-line md:text-lg"
              >
                {{ selectedDescription }}
              </p>
            </div>

            <!-- Spotify embed -->
            <div class="relative w-full overflow-hidden rounded-xl">
              <!-- Loader -->
              <div
                v-if="loadingIframe"
                class="absolute inset-0 flex flex-col items-center justify-center bg-gray-100"
              >
                <!-- Spinner -->
                <div
                  class="flex items-center justify-center w-12 h-12 mb-3 bg-gray-200 rounded-full"
                >
                  <svg
                    class="w-6 h-6 text-gray-600 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    ></path>
                  </svg>
                </div>

                <!-- Text -->
                <p class="text-sm font-medium text-gray-500">
                  {{ $t("loadingPlayer") }}
                </p>
              </div>

              <!-- Iframe -->
              <iframe
                :src="selectedArtist.spotify"
                width="100%"
                height="352"
                frameborder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                class="transition-opacity duration-500 rounded-xl"
                :class="loadingIframe ? 'opacity-0' : 'opacity-100'"
                @load="loadingIframe = false"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import type { Artist } from "~/types/artist";

const { t, locale } = useI18n();

const selectedArtist = ref<Artist | null>(null);
const artists = ref<Artist[]>([]);
const loading = ref(true);
const loadingIframe = ref(true);
const skeletonCount = ref(6); // Show 6 skeletons

// Fetch artists from API
async function fetchArtists() {
  try {
    const res = await $fetch<Artist[]>("/api/artists");
    // Sort active first, then name
    artists.value = res.sort((a, b) => {
      if (a.active !== b.active) return a.active ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  } catch (error) {
    console.error("Error fetching artists:", error);
  } finally {
    loading.value = false;
  }
}

// Open modal
function openModal(artist: Artist) {
  if (!artist.active) return;
  selectedArtist.value = artist;
}

// Reset iframe loader on modal change
watch(selectedArtist, () => {
  loadingIframe.value = true;
});

// Compute the description dynamically, reativo ao locale
const selectedDescription = computed(() => {
  if (!selectedArtist.value) return "";
  // Substitui _ por . para acessar o path no i18n
  return t(selectedArtist.value.description_key.replace("_", "."));
});

// Fetch artists on mount
onMounted(fetchArtists);

// Compute grid classes
const gridColsClass = computed(() => {
  const len = artists.value.length;
  if (len === 1) return "xl:grid-cols-1";
  if (len === 2) return "xl:grid-cols-2";
  if (len === 4) return "xl:grid-cols-2";
  return len > 6 ? "xl:grid-cols-4" : "xl:grid-cols-3";
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.animate-fade-in {
  animation: fadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@keyframes fadeIn {
  from {
    transform: translateY(20px) scale(0.98);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

/* Custom scrollbar for modal */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}
</style>
